import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../utils/colors';
import { usePortalAuth } from '../context/portalAuthContext';
import { setSurface } from '../utils/portalStorage';

/**
 * Customer portal login. Single "Email or mobile" field + password. The backend
 * normalizes the identifier, so we do NOT enforce a format. Distinguishes a
 * plain bad-credentials 401, an account-lockout 401, and a per-IP 429.
 */
const PortalLogin = () => {
  const { t } = useTranslation();
  const s = t('portal.login', { returnObjects: true });
  const { login, isLoading } = usePortalAuth();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || isLoading) return; // guard Enter-key double-submit
    setError('');
    if (!identifier.trim()) {
      setError(s.errors?.identifierRequired || 'Enter your email or mobile.');
      return;
    }
    setSubmitting(true);
    const res = await login(identifier.trim(), password);
    if (!res.success) {
      if (res.rateLimited) setError(s.errors?.rateLimited || 'Too many attempts. Please wait a minute and try again.');
      else if (res.locked) setError(s.errors?.locked || 'Too many failed attempts. Please wait ~15 minutes or contact support.');
      else setError(res.error || s.errors?.invalid || 'Incorrect email/mobile or password.');
    }
    setSubmitting(false);
  };

  const busy = submitting || isLoading;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: GRADIENTS.NEUTRAL, backgroundColor: UI_COLORS.BACKGROUNDS.PRIMARY }}
    >
      <div className="relative z-10 w-full max-w-md">
        <div
          className="backdrop-blur-xl rounded-2xl p-8 shadow-2xl border"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.CARD, 0.95),
            borderColor: UI_COLORS.BORDERS.LIGHT,
            boxShadow: `0 20px 25px -5px ${UI_COLORS.SHADOWS.MEDIUM}, 0 10px 10px -5px ${UI_COLORS.SHADOWS.LIGHT}`,
          }}
        >
          {/* Back to staff */}
          <button
            type="button"
            onClick={() => setSurface('staff')}
            className="text-sm font-medium mb-4 hover:underline"
            style={{ color: UI_COLORS.TEXT.ACCENT }}
          >
            ← {s.backToStaff || 'Staff login'}
          </button>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {s.title || 'Customer Portal'}
            </h2>
            <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>{s.subtitle || 'Sign in to view your orders and inquiries'}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="portal-identifier" className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                {s.identifierLabel || 'Email or mobile'}
              </label>
              <input
                id="portal-identifier"
                type="text"
                value={identifier}
                onChange={(e) => { setIdentifier(e.target.value); if (error) setError(''); }}
                className="w-full px-4 py-3 border-2 rounded-xl transition-all duration-200"
                style={{
                  backgroundColor: UI_COLORS.FORMS.INPUT.background,
                  borderColor: error ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                  color: UI_COLORS.FORMS.INPUT.text,
                }}
                placeholder={s.identifierPlaceholder || 'you@example.com or 9876543210'}
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="portal-password" className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                {s.passwordLabel || 'Password'}
              </label>
              <div className="relative">
                <input
                  id="portal-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                  className="w-full px-4 pr-12 py-3 border-2 rounded-xl transition-all duration-200"
                  style={{
                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                    borderColor: error ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                    color: UI_COLORS.FORMS.INPUT.text,
                  }}
                  placeholder={s.passwordPlaceholder || 'Your password'}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center hover:opacity-80"
                  style={{ color: UI_COLORS.TEXT.MUTED }}
                >
                  {showPassword ? (s.hide || 'Hide') : (s.show || 'Show')}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="border px-4 py-3 rounded-xl text-sm font-medium text-center"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                  borderColor: UI_COLORS.STATUS.ERROR,
                  color: UI_COLORS.STATUS.ERROR,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              style={{
                background: busy ? UI_COLORS.BUTTONS.PRIMARY.disabled : GRADIENTS.PRIMARY,
                color: UI_COLORS.BUTTONS.PRIMARY.text,
                boxShadow: `0 4px 14px 0 ${UI_COLORS.SHADOWS.COLORED}`,
              }}
            >
              {busy ? (s.submitLoading || 'Signing in…') : (s.submit || 'Sign in')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
